import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalText from 'components/container/HorizontalText'
import { AssetData } from 'constants/assets'
import CoreModal from 'core/CoreModal'
import CoreTokenInput from 'core/CoreTokenInput'
import RiskRatioBar from 'components/RiskRatioBar'
import { useMemo, useState } from 'react'
import { toAsset } from 'utils/contractParse'
import { Asset } from 'interfaces/contracts/edgePool'
import { toA } from 'utils/display'
import { observer } from 'mobx-react-lite'
import { accountStore, oracleStore, poolStore } from 'stores'
import { poolService } from 'services/poolService'
import { ONE, PERCENT, ZERO } from 'constants/number'
import ValueChange from 'components/container/ValueChange'
import { handleSupplyChange } from 'utils/handleChangeValue'
import { buttonStateHelper } from 'utils/buttonStateHelper'
import ModalTextSpacing from 'components/container/ModalTextSpacing'
import useSafeRatioHelper from 'hooks/useSafeRatioHelper'
import { ForceMaxRatio } from 'constants/constants'

interface Props {
  onClose: () => void
  sendWithdraw: (asset: Asset) => void
  assetData: AssetData & { etoken?: string }
}

const WithdrawModal = observer(
  ({ onClose, sendWithdraw, assetData }: Props) => {
    const eTokenBalance = accountStore.getBalanceOf(assetData.etoken)
    const poolAddr = poolService.poolAddress
    const {
      walletSupplyBalance,
      walletNetAPY,
      supplyAPY,
      walletBorrowingPowerUSD,
      walletTotalDebtValueUSD,
      walletCollateralValueUSD,
      marketInfo,
      locked,
      rateU2E,
    } = poolStore.getMarketOverall(poolAddr, assetData.terraAddress)
    const {
      totalCredit,
      config: { collateral_factor },
    } = marketInfo[assetData.terraAddress]
    const assetPrice = oracleStore.getAssetPrice(
      poolAddr,
      assetData.terraAddress,
    )
    const withdrawableBalance = BigNumber.min(walletSupplyBalance, totalCredit)
    const withdrawableBalanceUSD = withdrawableBalance?.times(assetPrice)
    const { forceMaxUSD } = useSafeRatioHelper({
      walletTotalDebtValueUSD,
      walletCollateralValueUSD,
      mode: 'withdraw',
    })
    const [amount, setAmount] = useState<string>('')
    const [afSupplyBal, afNetAPY, afBorrowingPower] = useMemo(() => {
      const amountUSD = (amount === '' ? ZERO : new BigNumber(amount))
        .times(assetPrice)
        .times(-1)

      const [_supply, _netAPY] = handleSupplyChange({
        supply: walletSupplyBalance.times(assetPrice),
        change: amountUSD,
        netAPY: walletNetAPY,
        supplyAPY,
      })
      const s = _supply && `${toA(_supply.div(assetPrice))} ${assetData.symbol}`
      const n = _netAPY && `${toA(_netAPY)}%`
      const b =
        _supply &&
        walletBorrowingPowerUSD &&
        `$${toA(
          walletBorrowingPowerUSD
            .plus(amountUSD.times(collateral_factor))
            .toFixed(2),
        )}`

      return [s, n, b]
    }, [
      amount,
      assetData.symbol,
      assetPrice,
      collateral_factor,
      supplyAPY,
      walletBorrowingPowerUSD,
      walletNetAPY,
      walletSupplyBalance,
    ])

    const maxWithdrawWithHealthy = useMemo(() => {
      if (walletBorrowingPowerUSD && locked) {
        return BigNumber.min(
          walletBorrowingPowerUSD.div(collateral_factor),
          withdrawableBalanceUSD,
          forceMaxUSD.div(collateral_factor),
        ).div(assetPrice)
      } else {
        return withdrawableBalance
      }
    }, [
      assetPrice,
      collateral_factor,
      forceMaxUSD,
      locked,
      walletBorrowingPowerUSD,
      withdrawableBalanceUSD,
      withdrawableBalance,
    ])

    const newLTV = useMemo(() => {
      const amountUSD = (amount === '' ? ZERO : new BigNumber(amount))
        .times(assetPrice)
        .times(collateral_factor)

      if (!walletTotalDebtValueUSD || !walletCollateralValueUSD) return ZERO
      if (walletCollateralValueUSD.eq(ZERO)) return ZERO
      if (!locked) {
        return walletTotalDebtValueUSD
          .div(walletCollateralValueUSD)
          .times(PERCENT)
      }
      return walletTotalDebtValueUSD
        .div(walletCollateralValueUSD.minus(amountUSD))
        .times(PERCENT)
    }, [
      amount,
      assetPrice,
      collateral_factor,
      locked,
      walletCollateralValueUSD,
      walletTotalDebtValueUSD,
    ])

    const { state: btnState, disabled } = buttonStateHelper({
      forceDisable: newLTV.gt(ForceMaxRatio) && locked,
      amount,
      max: withdrawableBalance,
      needApprove: false,
      state: {
        zeroMax: 'No withdrawable amount',
        overflowAmount: 'Not enough withdrawable balance',
        actual: 'Withdraw',
      },
    })

    const handleOnSend = () => {
      if (!assetData.etoken) {
        onClose()
        return
      }
      let _amount: BigNumber
      if (totalCredit.eq(amount) && !totalCredit.eq(walletSupplyBalance)) {
        _amount = rateU2E
          .times(amount)
          .minus(ONE.div(`1e${assetData.decimals - 5}`))
      } else {
        _amount = rateU2E.times(amount)
      }
      _amount = BigNumber.min(eTokenBalance, _amount)
      sendWithdraw(
        toAsset(
          assetData.etoken,
          _amount.times(new BigNumber(`1e${assetData?.decimals}`)),
        ),
      )
      onClose()
    }

    const Content = (
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="h5">Withdraw</Typography>
        <br />
        {assetData && (
          <CoreTokenInput
            value={amount}
            assetDatas={[assetData]}
            onChange={(v) => setAmount(v)}
            maxValue={maxWithdrawWithHealthy}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            margin: '32px 0px 16px 0px',
          }}
        >
          <HorizontalText
            left="Withdrawable balance"
            right={`${toA(withdrawableBalance)} ${assetData?.symbol} ($${toA(
              withdrawableBalanceUSD,
            )})`}
          />
          <ModalTextSpacing
            itl={<Typography variant="body2">My supply balance</Typography>}
            itr={
              <ValueChange
                bf={`${toA(walletSupplyBalance)} ${assetData.symbol}`}
                af={afSupplyBal}
              />
            }
          />
          <ModalTextSpacing
            itl={<Typography variant="body2">Net APY</Typography>}
            itr={<ValueChange bf={`${toA(walletNetAPY)}%`} af={afNetAPY} />}
          />
          {locked && (
            <ModalTextSpacing
              itl={
                <Typography variant="body2">Borrowing power left</Typography>
              }
              itr={
                <ValueChange
                  bf={`$${toA(walletBorrowingPowerUSD)}`}
                  af={afBorrowingPower}
                />
              }
            />
          )}
        </Box>
        {locked && (
          <RiskRatioBar
            RiskRatio={newLTV}
            title="Risk ratio"
            sx={{ marginBottom: '32px' }}
            showFullText
          />
        )}
        <Button
          variant="contained"
          fullWidth
          onClick={handleOnSend}
          disabled={disabled}
        >
          {btnState}
        </Button>
      </Box>
    )
    return <CoreModal open content={Content} onClose={onClose} />
  },
)

export default WithdrawModal
